import { Controller } from "@hotwired/stimulus"

// Form-level controller for editing an invoice that carries product line items.
//
// Lowering a product's quantity takes goods back off the invoice, but only the admin
// knows whether they physically came back to the counter — so before the edit saves we
// intercept the submit, list what went down, and let them choose whether to return the
// difference to inventory. The choice posts as a `restock` param (see EditInvoice).
//
// Rows that aren't Retail have their product/quantity inputs disabled by the per-row
// invoice-line-item controller, so they're skipped here for free.
export default class extends Controller {
  static targets = ["reductions", "restock"]

  // {productId: {name, quantity}} as the invoice stands before this edit.
  static values = { originalProducts: Object }

  // Submit straight through unless something went down and we haven't asked yet.
  confirm(e) {
    if (this.restockTarget.value !== "") return

    const reductions = this.reductions()
    if (reductions.length === 0) return

    e.preventDefault()
    this.renderReductions(reductions)
    this.element.querySelector("[data-restock-dialog-activator]")?.click()
  }

  restockAndSubmit() {
    this.submitWith("true")
  }

  submitWithoutRestocking() {
    this.submitWith("false")
  }

  submitWith(value) {
    this.restockTarget.value = value
    this.element.requestSubmit()
  }

  // The products whose submitted quantity is below what the invoice currently carries.
  // A product dropped from the invoice entirely counts as going to zero.
  reductions() {
    const submitted = this.submittedQuantities()

    return Object.entries(this.originalProductsValue)
      .map(([productId, { name, quantity }]) => ({
        difference: quantity - (submitted[productId] || 0),
        name,
      }))
      .filter(({ difference }) => difference > 0)
  }

  submittedQuantities() {
    const quantities = {}

    this.element
      .querySelectorAll("[name='create_simple_invoice[line_items][][product_id]']:not([disabled])")
      .forEach((field) => {
        if (field.value === "") return

        const row = field.closest("[data-controller~='invoice-line-item']")
        const quantityField = row?.querySelector(
          "[name='create_simple_invoice[line_items][][quantity]']",
        )
        const quantity = parseInt(quantityField?.value, 10) || 0

        quantities[field.value] = (quantities[field.value] || 0) + quantity
      })

    return quantities
  }

  renderReductions(reductions) {
    this.reductionsTarget.replaceChildren(
      ...reductions.map(({ difference, name }) => {
        const li = document.createElement("li")
        li.textContent = `${difference} × ${name}`
        return li
      }),
    )
  }
}
